import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/i18n/LocaleContext";
import { ApiError } from "@/lib/api";
import type { RequestType } from "@/lib/employee-api";
import { requestTypeLabel } from "@/lib/request-labels";
import {
  settingsApi,
  type RequestSettingsSlice,
  type RequestTypeView,
  type TenantSettings,
} from "@/lib/settings-api";

/** Match the server's limits (BR-210, BR-213). */
const INSTRUCTION_MAX = 500;
const LABEL_MAX = 40;
const DESCRIPTION_MAX = 200;

type TypeState = {
  offered: boolean;
  label: string;
  description: string;
  dateMode: "single" | "range";
  required: boolean;
  instruction: string;
};
type FormState = { approval: boolean; types: Record<string, TypeState> };

type Props = {
  initial: RequestSettingsSlice;
  onSaved: (settings: TenantSettings) => void;
};

/** The offerable types, in the app's order (Shift swap is retired). */
const OFFERABLE: RequestType[] = [
  "MISSED_CHECK_IN",
  "MISSED_CHECK_OUT",
  "INCORRECT_LOCATION",
  "ATTENDANCE_CORRECTION",
  "LEAVE",
  "OVERTIME",
  "WORK_FROM_ANYWHERE",
  "OTHER",
];

/**
 * The rows to edit. An API that predates FEAT-2026-031 sends no
 * `requestTypes` (deploy ships the portal and the API in parallel), so the
 * rows are built from the attachment rules and only those can be changed.
 */
function rowsFor(s: RequestSettingsSlice): RequestTypeView[] {
  if (Array.isArray(s.requestTypes) && s.requestTypes.length > 0) return s.requestTypes;
  return OFFERABLE.map((type) => {
    const rule = s.requestAttachmentRules.find((r) => r.type === type);
    return {
      type,
      offered: true,
      label: null,
      description: null,
      dateMode: type === "LEAVE" || type === "WORK_FROM_ANYWHERE" ? "range" : "single",
      dateModeConfigurable: false,
      attachment: { required: rule?.required ?? false, instruction: rule?.instruction ?? null },
      defaultName: requestTypeLabel(type),
    };
  });
}

function toForm(s: RequestSettingsSlice): FormState {
  const types = Object.fromEntries(
    rowsFor(s).map((t) => [
      t.type,
      {
        offered: t.offered,
        label: t.label ?? "",
        description: t.description ?? "",
        dateMode: t.dateMode,
        required: t.attachment.required,
        instruction: t.attachment.instruction ?? "",
      },
    ]),
  );
  return { approval: s.lineManagerApprovalEnabled, types };
}

/**
 * Settings → Requests: whether line managers decide their direct reports'
 * requests (BR-211, FEAT-2026-027), and, for each request type, whether staff
 * are offered it, its name and description, its dates (Other only) and its
 * attachment rule (BR-210/212/213, FEAT-2026-031). Saves on its own through
 * `PATCH /settings/requests`, which HR Manager may call. Shift swap is retired
 * and isn't listed.
 */
export default function RequestsSettingsTab({ initial, onSaved }: Props) {
  const t = useT();
  const [saved, setSaved] = useState<FormState>(() => toForm(initial));
  const [form, setForm] = useState<FormState>(saved);
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const rows = useMemo(() => rowsFor(initial), [initial]);
  // False against an API without FEAT-2026-031: attachment rules only.
  const typesSupported = Array.isArray(initial.requestTypes) && initial.requestTypes.length > 0;

  // R1: staff must always be able to raise something. Work from anywhere only
  // reaches staff while the tenant has WFA on, so it doesn't count otherwise.
  const reachesStaff = (type: RequestType) =>
    form.types[type]?.offered && (type !== "WORK_FROM_ANYWHERE" || initial.wfaEnabled);
  const offeredCount = rows.filter((rt) => reachesStaff(rt.type)).length;

  const setType = (type: RequestType, patch: Partial<TypeState>) =>
    setForm((f) => ({ ...f, types: { ...f.types, [type]: { ...f.types[type], ...patch } } }));

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await settingsApi.updateRequests({
        lineManagerApprovalEnabled: form.approval,
        attachmentRules: rows.map(({ type }) => ({
          type,
          required: form.types[type].required,
          instruction: form.types[type].instruction.trim() || null,
        })),
        ...(typesSupported
          ? {
              types: rows.map(({ type, dateModeConfigurable }) => {
                const s = form.types[type];
                return {
                  key: type,
                  offered: s.offered,
                  label: s.label.trim() || null,
                  description: s.description.trim() || null,
                  // The server refuses a date mode on a type whose dates are fixed.
                  ...(dateModeConfigurable ? { dateMode: s.dateMode } : {}),
                };
              }),
            }
          : {}),
      });
      setSaved(form);
      onSaved(updated);
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("settings.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-4">
        <div>
          <h3 className="font-display font-semibold">{t("settings.requests.approvalTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("settings.requests.approvalDesc")}</p>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="text-sm font-medium">{t("settings.requests.approvalToggle")}</div>
          <Switch
            aria-label={t("settings.requests.approvalToggle")}
            checked={form.approval}
            onCheckedChange={(v) => setForm((f) => ({ ...f, approval: v }))}
          />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="font-display font-semibold">{t("settings.requests.typesTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("settings.requests.typesDesc")}</p>
        </div>
        <div className="space-y-3">
          {rows.map(({ type, defaultName, dateModeConfigurable }) => {
            const s = form.types[type];
            // The last type reaching staff can't be switched off (R1).
            const isLast = offeredCount === 1 && reachesStaff(type);
            return (
              <div
                key={type}
                data-testid="request-type"
                data-type={type}
                className="space-y-3 rounded-lg border p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">{defaultName}</div>
                  {typesSupported && <div className="flex items-center gap-2">
                    {isLast && (
                      <span className="text-xs text-muted-foreground">{t("settings.requests.lastOffered")}</span>
                    )}
                    <span className="text-xs text-muted-foreground">{t("settings.requests.offered")}</span>
                    <Switch
                      aria-label={`${t("settings.requests.offered")}: ${defaultName}`}
                      checked={s.offered}
                      disabled={isLast}
                      onCheckedChange={(v) => setType(type, { offered: v })}
                    />
                  </div>}
                </div>
                {typesSupported && type === "WORK_FROM_ANYWHERE" && !initial.wfaEnabled && (
                  <p className="text-xs text-muted-foreground">{t("settings.requests.wfaNote")}</p>
                )}
                <div className={`grid gap-3 sm:grid-cols-2 ${s.offered ? "" : "opacity-60"}`}>
                  {typesSupported && <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">{t("settings.requests.name")}</div>
                    <Input
                      aria-label={`${t("settings.requests.name")}: ${defaultName}`}
                      maxLength={LABEL_MAX}
                      placeholder={defaultName}
                      value={s.label}
                      onChange={(e) => setType(type, { label: e.target.value })}
                    />
                  </div>}
                  {typesSupported && <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">{t("settings.requests.description")}</div>
                    <Input
                      aria-label={`${t("settings.requests.description")}: ${defaultName}`}
                      maxLength={DESCRIPTION_MAX}
                      placeholder={t("settings.requests.descriptionPlaceholder")}
                      value={s.description}
                      onChange={(e) => setType(type, { description: e.target.value })}
                    />
                  </div>}
                  {dateModeConfigurable && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted-foreground">{t("settings.requests.dates")}</div>
                      <Select value={s.dateMode} onValueChange={(v) => setType(type, { dateMode: v as TypeState["dateMode"] })}>
                        <SelectTrigger aria-label={`${t("settings.requests.dates")}: ${defaultName}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">{t("settings.requests.single")}</SelectItem>
                          <SelectItem value="range">{t("settings.requests.range")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1 sm:col-span-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        aria-label={`${t("settings.requests.require")}: ${defaultName}`}
                        checked={s.required}
                        onCheckedChange={(v) => setType(type, { required: v })}
                      />
                      <span className="text-xs font-medium text-muted-foreground">{t("settings.requests.require")}</span>
                    </div>
                    <Textarea
                      aria-label={`${t("settings.requests.instruction")}: ${defaultName}`}
                      rows={2}
                      maxLength={INSTRUCTION_MAX}
                      placeholder={t("settings.requests.instructionPlaceholder")}
                      value={s.instruction}
                      onChange={(e) => setType(type, { instruction: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setForm(saved)} disabled={!dirty || saving}>
          {t("common.cancel")}
        </Button>
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}
